import { Injectable } from '@angular/core';
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';
import { Space, Note } from '../models/space.model';
import { ToastService } from './toast.service';
import { MatomoService } from './matomo.service';

const MAX_URL_LENGTH = 1800;

interface ShareableSpace {
  i?: string;          // id (optional for backward compat with old links)
  n: string;           // name
  c: string;           // country
  ri: string[];        // relevantIndustries
  d?: string[];        // disciplines
  ar?: string[];       // legacy: areasOfResponsibility
  rm: string[];        // relevantMaterials
  dc: string;          // dateCreated (ISO string)
  on: string;          // ownerName
  st: 'A' | 'I';       // status (shortened)
  ts: number;          // teamSize
  ah: number;          // averageHourlyRate
  py: number;          // projectsPerYear
  al: number;          // averageLicenceCost
  ml: number;          // maturityLevel
  pg?: number;         // productivityGainPercent
  rr?: number;         // reworkReductionPercent
  cs?: number;         // coordinationSavingsPercent
  ss?: string[];       // selectedSolutions
  se?: string[];       // selectedEditions
  bpd?: number[];       // bookmarkedProductDemos
  bwd?: number[];       // bookmarkedWorkflowDemos
  buc?: number[];       // bookmarkedUseCases
  nt?: ShareableNote[];// notes (optional, may be excluded if too large)
}

interface ShareableNote {
  t: string;   // text
  cr: string;  // creator
  ca: string;  // createdAt
}

@Injectable({
  providedIn: 'root'
})
export class ShareService {

  constructor(
    private readonly toastService: ToastService,
    private readonly matomoService: MatomoService
  ) {}

  /**
   * Serializes a Space to a URL-safe compressed string
   * Returns null if the result would be too long for a URL
   */
  serializeSpace(space: Space, includeNotes = true): string | null {
    const shareable: ShareableSpace = {
      i: space.id,
      n: space.name,
      c: space.country,
      ri: space.relevantIndustries,
      d: space.disciplines,
      rm: space.relevantMaterials,
      dc: space.dateCreated.toISOString(),
      on: space.ownerName,
      st: space.status === 'Active' ? 'A' : 'I',
      ts: space.teamSize,
      ah: space.averageHourlyRate,
      py: space.projectsPerYear,
      al: space.averageLicenceCost,
      ml: space.maturityLevel,
      pg: space.productivityGainPercent,
      rr: space.reworkReductionPercent,
      cs: space.coordinationSavingsPercent
    };

    if (space.bookmarkedProductDemos?.length > 0) {
      shareable.bpd = space.bookmarkedProductDemos;
    }
    if (space.bookmarkedWorkflowDemos?.length > 0) {
      shareable.bwd = space.bookmarkedWorkflowDemos;
    }
    if (space.bookmarkedUseCases?.length > 0) {
      shareable.buc = space.bookmarkedUseCases;
    }

    if (space.selectedSolutions?.length > 0) {
      shareable.ss = space.selectedSolutions;
    }
    if (space.selectedEditions?.length > 0) {
      shareable.se = space.selectedEditions;
    }

    // Include notes if requested and space has notes
    if (includeNotes && space.notes.length > 0) {
      shareable.nt = space.notes.map(note => ({
        t: note.text,
        cr: note.creator,
        ca: note.createdAt.toISOString()
      }));
    }

    const json = JSON.stringify(shareable);
    let compressed = compressToEncodedURIComponent(json);

    // If too large with notes, try without notes
    if (compressed.length > MAX_URL_LENGTH && includeNotes && space.notes.length > 0) {
      return this.serializeSpace(space, false);
    }

    // Still too large
    if (compressed.length > MAX_URL_LENGTH) {
      return null;
    }

    return compressed;
  }

  /**
   * Deserializes a compressed string back to a Space object
   */
  deserializeSpace(encoded: string): Space | null {
    try {
      const json = decompressFromEncodedURIComponent(encoded);
      if (!json) return null;

      const shareable: ShareableSpace = JSON.parse(json);

      const notes: Note[] = (shareable.nt ?? []).map(n => ({
        text: n.t,
        creator: n.cr,
        createdAt: new Date(n.ca)
      }));

      const space: Space = {
        id: shareable.i ?? crypto.randomUUID(),
        name: shareable.n,
        country: shareable.c,
        notes,
        relevantIndustries: shareable.ri,
        disciplines: shareable.d ?? shareable.ar ?? [],
        relevantMaterials: shareable.rm,
        selectedSolutions: shareable.ss ?? [],
        selectedEditions: shareable.se ?? [],
        dateCreated: new Date(shareable.dc),
        ownerName: shareable.on,
        status: shareable.st === 'A' ? 'Active' : 'Inactive',
        teamSize: shareable.ts,
        averageHourlyRate: shareable.ah,
        projectsPerYear: shareable.py,
        averageLicenceCost: shareable.al,
        maturityLevel: shareable.ml ?? 20,
        productivityGainPercent: shareable.pg ?? 0,
        reworkReductionPercent: shareable.rr ?? 0,
        coordinationSavingsPercent: shareable.cs ?? 0,
        bookmarkedProductDemos: shareable.bpd ?? [],
        bookmarkedWorkflowDemos: shareable.bwd ?? [],
        bookmarkedUseCases: shareable.buc ?? []
      };

      return space;
    } catch (e) {
      console.error('Failed to deserialize space:', e);
      return null;
    }
  }

  /**
   * Generates a share URL for a space and copies it to clipboard
   */
  async shareSpace(space: Space): Promise<boolean> {
    const encoded = this.serializeSpace(space);

    if (!encoded) {
      this.matomoService.trackActionPageView(
        this.matomoService.CATEGORY.share,
        this.matomoService.ACTION.copyLinkFailed,
        'share_link'
      );
      this.toastService.error('Space is too large to share via URL');
      return false;
    }

    const shareUrl = `${globalThis.location.origin}/spaces/import?data=${encoded}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      this.matomoService.trackActionPageView(
        this.matomoService.CATEGORY.share,
        this.matomoService.ACTION.copyLinkSuccess,
        'share_link'
      );
      this.toastService.success('Share link copied to clipboard!');
      return true;
    } catch (e) {
      this.matomoService.trackActionPageView(
        this.matomoService.CATEGORY.share,
        this.matomoService.ACTION.copyLinkFailed,
        'share_link'
      );
      console.error('Failed to copy to clipboard:', e);
      this.toastService.error('Failed to copy link to clipboard');
      return false;
    }
  }

  /**
   * Generates a share URL for a space
   */
  getShareUrl(space: Space): string | null {
    const encoded = this.serializeSpace(space);
    if (!encoded) return null;
    return `${globalThis.location.origin}/spaces/import?data=${encoded}`;
  }

  /**
   * Opens the default email client with a pre-filled message containing the share link
   */
  shareSpaceViaEmail(space: Space, recipientEmail = ''): boolean {
    const shareUrl = this.getShareUrl(space);

    if (!shareUrl) {
      this.matomoService.trackActionPageView(
        this.matomoService.CATEGORY.share,
        this.matomoService.ACTION.emailShareFailed,
        'share_link'
      );
      this.toastService.error('Space is too large to share via URL');
      return false;
    }

    const subject = encodeURIComponent(`Check out this Space: ${space.name}`);
    const body = encodeURIComponent(
      `Hi,\n\n` +
      `I'm sharing a space "${space.name}" with you from Amplify.\n\n` +
      `Click the link below to import it:\n${shareUrl}\n\n` +
      `Best regards`
    );

    const mailtoUrl = `mailto:${recipientEmail}?subject=${subject}&body=${body}`;
    
    globalThis.open(mailtoUrl, '_self');
    this.matomoService.trackActionPageView(
      this.matomoService.CATEGORY.share,
      this.matomoService.ACTION.emailShareTriggered,
      'share_link'
    );
    this.toastService.info('Opening email client...');
    return true;
  }

  /**
   * Copies to clipboard AND opens email client
   */
  async shareSpaceWithEmail(space: Space): Promise<boolean> {
    const success = await this.shareSpace(space);
    if (success) {
      // Small delay so user sees the "copied" toast first
      setTimeout(() => this.shareSpaceViaEmail(space), 500);
    }
    return success;
  }
}
